/*======================================================================================================================
  * Bluebell: src/app/state/sandbox/sandbox.reducer.ts
  * Last Edited: 31/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import {
  createSandboxSuccess,
  deleteSandboxSuccess,
  deselectSandbox,
  listSandboxes,
  listSandboxesFailure,
  listSandboxesSuccess,
  selectSandbox, updateSandboxSuccess,
} from '@state/sandbox/sandbox.actions';
import { Sandbox } from '@state/sandbox/sandbox.interfaces';


export interface State extends EntityState<Sandbox> {
  loading: boolean;
  selectedId: string|null;
}

export const adapter = createEntityAdapter<Sandbox>({
  selectId: (sandbox: Sandbox) => sandbox.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  loading: false,
  selectedId: null,
});

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({ ...initialState });
  }),

  /**
   * Sandbox CRUD
   */

  on(createSandboxSuccess, (state, { sandbox }) => {
    return adapter.addOne(
      sandbox,
      {
        ...state,
        selectedId: sandbox.uuid,
      },
    );
  }),

  on(listSandboxes, (state) => {
    return { ...state, loading: true };
  }),

  on(listSandboxesSuccess, (state, { sandboxes }) => {
    return adapter.setAll(sandboxes, { ...state, loading: false });
  }),

  on(listSandboxesFailure, (state) => {
    return { ...state, loading: false };
  }),

  on(updateSandboxSuccess, (state, { sandbox }) => {
    return adapter.upsertOne(sandbox, state);
  }),

  on(deleteSandboxSuccess, (state, { uuid }) => {
    return adapter.removeOne(
      uuid,
      {
        ...state,
        selectedId: state.selectedId === uuid ? null : state.selectedId,
      },
    );
  }),


  /**
   * Misc Actions
   */

  on(selectSandbox, (state, { selectedId }) => {
    return  { ...state, selectedId };
  }),

  on(deselectSandbox, (state) => {
    return { ...state, selectedId: null };
  }),

);

export const featureKey = 'sandboxes';
export const {
  selectEntities: getEntities,
  selectTotal: getCount,
  selectAll: getAll,
  selectIds: getIds,
} = adapter.getSelectors();
export const getLoading = (state: State) => state.loading;
export const getSelectedId = (state: State) => state.selectedId;
